import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Check, Search, Send, UserPlus } from 'lucide-react';
import { partnerSessionsApi } from '../../../services/partnerSessionsApi';
import { agentsApi } from '../../../services/agentsApi';
import type { SessionPerson } from '../../../types/aai';
import { Action, ErrorNote, Field, Input, Loading, Modal, Select } from '../../../components/aa';
import { REMIND_CHOICES, ROLE_GROUPS, errorText, inGroup, matches, plural, roleLabel } from './sessionShared';
import type { RoleGroup } from './sessionShared';

/*
 * Who the session goes to, in a dialog over the draft: everyone at the client, filtered by role or
 * searched, ticked one by one or all at once. Someone who is not in the
 * company yet is added here with a role and ticked; without a login they
 * answer from the emailed link.
 */

const parseRemind = (raw: string): number | null => {
  if (!raw) return null;
  return Number(raw);
};

const ADDABLE_ROLES: { value: string; label: string }[] = [
  { value: 'AI_PARTICIPANT', label: 'Employee' },
  { value: 'DEPARTMENT_HEAD', label: 'Department head' },
  { value: 'AI_CHAMPION', label: 'AI Champion' },
];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmails = (raw: string): string[] => {
  const out: string[] = [];
  raw.split(/[\s,;]+/).forEach((part) => {
    const email = part.trim().toLowerCase();
    if (email && !out.includes(email)) out.push(email);
  });
  return out;
};

const AddPerson: React.FC<{ clientId: string; onAdded: (emails: string[]) => void; onCancel: () => void }> = ({ clientId, onAdded, onCancel }) => {
  const [name, setName] = useState('');
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState('AI_PARTICIPANT');
  const [invite, setInvite] = useState(false);
  const [busy, setBusy] = useState(false);

  const list = parseEmails(emails);
  const invalid = list.filter((e) => !EMAIL.test(e));
  let reason = '';
  if (invalid.length > 0) reason = `Check ${invalid[0]}`;
  if (list.length === 0) reason = 'Enter at least one work email';
  if (busy) reason = 'Adding';
  let addLabel = 'Add and select';
  if (list.length > 1) addLabel = `Add ${list.length} and select`;

  const add = async () => {
    setBusy(true);
    const added: string[] = [];
    const failed: string[] = [];
    for (const email of list) {
      try {
        let personName: string | undefined;
        if (list.length === 1 && name.trim()) personName = name.trim();
        await agentsApi.addWorkspaceMember(clientId, { email, name: personName, role, send_email: invite });
        added.push(email);
      } catch (err) {
        failed.push(email);
        toast.error(`${email}: ${errorText(err, 'could not be added.')}`);
      }
    }
    setBusy(false);
    if (added.length === 0) return;
    let note = `${plural(added.length, 'person', 'people')} added to the company.`;
    if (invite) note = `${plural(added.length, 'person', 'people')} added and invited to set up a login.`;
    toast.success(note);
    if (failed.length > 0) setEmails(failed.join('\n'));
    onAdded(added);
  };

  return (
    <div className="mb-4 rounded-[14px] border border-line bg-canvas p-4">
      <strong className="block text-[13.5px] text-ink">Add people who are not set up yet</strong>
      <small className="mb-3 block text-[12px] leading-[18px] text-ink-muted">
        They join the company&apos;s People with this role and get the session by email. They answer from the link in it, no login needed.
      </small>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Work emails" htmlFor="rd-emails" hint="One or more, separated by commas or new lines." className="sm:col-span-2">
          <textarea
            id="rd-emails"
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            rows={2}
            placeholder="name@company.com"
            className="w-full rounded-act border border-line-strong bg-white px-3 py-2 text-[13.5px] text-ink outline-none focus:border-ink"
          />
        </Field>
        {list.length <= 1 && <Field label="Name" htmlFor="rd-name" hint="Optional."><Input id="rd-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" /></Field>}
        <Field label="Role" htmlFor="rd-role">
          <Select id="rd-role" value={role} onChange={(e) => setRole(e.target.value)}>
            {ADDABLE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Select>
        </Field>
      </div>
      <label className="mt-3 flex items-center gap-2 text-[12.5px] text-ink">
        <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} />
        Also invite them to set up a login now
      </label>
      <div className="mt-3 flex justify-end gap-2">
        <Action size="sm" variant="ghost" onClick={onCancel}>Cancel</Action>
        <Action size="sm" onClick={add} disabled={!!reason} reason={reason}>
          {addLabel}
        </Action>
      </div>
    </div>
  );
};

export const RecipientDrawer: React.FC<{
  clientId: string;
  title: string;
  sending: boolean;
  onClose: () => void;
  onSend: (emails: string[], remindEveryDays: number | null) => void;
}> = ({ clientId, title, sending, onClose, onSend }) => {
  const [remind, setRemind] = useState<number | null>(2);
  const [people, setPeople] = useState<SessionPerson[] | null>(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [group, setGroup] = useState<RoleGroup>('all');
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await partnerSessionsApi.people(clientId);
      setPeople(r.data.people);
    } catch (err) {
      setError(errorText(err, 'Could not load the people.'));
    }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const everyone = useMemo(() => people || [], [people]);
  const shown = everyone.filter((p) => inGroup(p.role, group) && matches(query, p.name, p.email, p.department_name));
  const allShownPicked = shown.length > 0 && shown.every((p) => picked.has(p.email));

  const toggle = (email: string) => setPicked((now) => {
    const next = new Set(now);
    if (next.has(email)) next.delete(email);
    else next.add(email);
    return next;
  });
  const toggleShown = () => setPicked((now) => {
    const next = new Set(now);
    shown.forEach((p) => {
      if (allShownPicked) next.delete(p.email);
      else next.add(p.email);
    });
    return next;
  });
  const added = async (emails: string[]) => {
    setAdding(false);
    await load();
    setPicked((now) => {
      const next = new Set(now);
      emails.forEach((e) => next.add(e));
      return next;
    });
  };

  let sendLabel = 'Choose people';
  if (picked.size > 0) sendLabel = `Send to ${plural(picked.size, 'person', 'people')}`;

  let body: React.ReactNode = <Loading label="Loading the people" />;
  if (error) body = <ErrorNote message={error} />;
  if (people) {
    body = (
      <div>
        {adding && <AddPerson clientId={clientId} onAdded={added} onCancel={() => setAdding(false)} />}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {ROLE_GROUPS.map((g) => {
            const count = everyone.filter((p) => inGroup(p.role, g.key)).length;
            let tone = 'border-line bg-white text-ink-muted hover:border-ink hover:text-ink';
            if (group === g.key) tone = 'border-ink bg-ink text-white';
            return (
              <button key={g.key} type="button" onClick={() => setGroup(g.key)} className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors ${tone}`}>
                {g.label} <span className="opacity-70">{count}</span>
              </button>
            );
          })}
        </div>
        <div className="relative mb-3">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" aria-hidden />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, email or department" className="!pl-9" aria-label="Search people" />
        </div>
        {shown.length === 0 && (
          <div className="rounded-[14px] border border-dashed border-line px-5 py-6 text-center text-[13px] text-ink-dim">
            Nobody matches. Clear the search, pick another role, or add someone new.
          </div>
        )}
        {shown.length > 0 && (
          <div className="overflow-hidden rounded-[14px] border border-line bg-white">
            <button type="button" onClick={toggleShown} className="flex w-full items-center gap-3 border-b border-line-soft bg-canvas px-4 py-2.5 text-left text-[12.5px] font-semibold text-ink">
              <Box on={allShownPicked} />
              Select all {shown.length} shown
            </button>
            {shown.map((p) => (
              <button key={p.email} type="button" onClick={() => toggle(p.email)} className="flex w-full items-center gap-3 border-t border-line-soft px-4 py-3 text-left first:border-t-0 hover:bg-canvas">
                <Box on={picked.has(p.email)} />
                <span className="min-w-0 flex-1">
                  <b className="block truncate text-[13.5px] text-ink">{p.name || p.email}</b>
                  <small className="block truncate text-[12px] text-ink-dim">{[p.email, p.department_name].filter(Boolean).join(' · ')}</small>
                </span>
                <span className="shrink-0 rounded-full bg-canvas-inset px-2.5 py-1 text-[11px] font-semibold text-ink-muted">{roleLabel(p.role)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Modal
      onClose={onClose}
      size="dialog"
      context="Send session"
      badge={!adding && <Action size="sm" variant="outline" icon={<UserPlus size={14} />} onClick={() => setAdding(true)}>Add people</Action>}
      footer={(
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[12.5px] text-ink-muted">{plural(picked.size, 'person', 'people')} selected</span>
            <label className="inline-flex items-center gap-2 text-[12.5px] text-ink-muted">
              Remind who has not answered
              <Select
                value={String(remind ?? '')}
                onChange={(e) => setRemind(parseRemind(e.target.value))}
                aria-label="Remind people who have not answered"
                className="!h-9 !w-auto !py-0 !text-[12.5px] leading-none"
              >
                {REMIND_CHOICES.map((c) => <option key={c.label} value={String(c.value ?? '')}>{c.label}</option>)}
              </Select>
            </label>
          </div>
          <div className="flex gap-2">
            <Action variant="ghost" onClick={onClose}>Back to the draft</Action>
            <Action icon={<Send size={14} />} onClick={() => onSend(Array.from(picked), remind)} disabled={picked.size === 0 || sending} reason="Sending">{sendLabel}</Action>
          </div>
        </div>
      )}
    >
      <div className="min-h-[420px]">
        <h2 className="m-0 text-[20px] font-bold leading-[26px] text-ink">{title || 'Untitled session'}</h2>
        <p className="m-0 mb-4 mt-1 text-[13px] leading-[19px] text-ink-muted">Choose who receives it. Everyone gets an email: people with a login answer in their AI Champion, people without one answer from the link. Reminders stop once they answer, and after three.</p>
        {body}
      </div>
    </Modal>
  );
};

const Box: React.FC<{ on: boolean }> = ({ on }) => {
  let tone = 'border-line-strong bg-white';
  if (on) tone = 'border-ink bg-ink text-white';
  return (
    <span className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded-[5px] border ${tone}`} aria-hidden>
      {on && <Check size={12} strokeWidth={3} />}
    </span>
  );
};

export default RecipientDrawer;
